@if (mode === 'link') {
  <div class="min-h-[60vh] flex items-center justify-center px-4 py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-md p-8 max-w-md w-full text-center space-y-4">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary mx-auto"></span>
      <h2 class="text-xl font-bold text-base-content">Redirecting to Privacy Policy...</h2>
      <p class="text-xs text-base-content/70">
        You are being redirected to {{ academyName }}'s external Privacy Policy.
      </p>
      @if (externalUrl) {
        <div class="pt-2">
          <a [href]="externalUrl" class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2">
            <span>Click here if not redirected</span>
            <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i>
          </a>
        </div>
      }
    </div>
  </div>
} @else if (mode === 'custom') {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-3xl mx-auto">
      <span
        class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70"
      >
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">
        Privacy Policy
      </h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        Learn how {{ academyName }} protects, manages, and respects your personal information.
      </p>
      @if (lastUpdated) {
        <p class="text-xs text-base-content/60 mt-3 flex items-center justify-center gap-1.5">
          <i class="fa-regular fa-calendar"></i>
          <span>Last Updated: {{ lastUpdated | date:'mediumDate' }}</span>
        </p>
      }
    </div>
  </div>

  <!-- Custom Page Body -->
  <div class="max-w-4xl mx-auto px-6 py-10 md:py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-2xl p-6 sm:p-10">
      <div class="ql-snow">
        <div class="ql-editor !p-0 max-w-none text-base-content leading-relaxed" [innerHTML]="customContent"></div>
      </div>
    </div>
  </div>
} @else {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-2xl mx-auto">
      <span
        class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70"
      >
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">
        Privacy Policy
      </h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        At {{ academyName }}, we are committed to protecting your personal information.
        This Privacy Policy is aligned with the Personal Data Protection Act, No.
        9 of 2022 (PDPA) of Sri Lanka.
      </p>
    </div>
  </div>

  <!-- Page Body -->
  <div
    class="flex flex-col md:flex-row gap-10 max-w-6xl mx-auto px-6 py-10 md:py-16 items-start"
  >
    <!-- Left Sidebar -->
    <aside class="w-full md:w-60 md:shrink-0 md:sticky md:top-20">
      <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-xl p-6">
        <h2 class="text-base font-bold text-base-content mb-2">At a Glance</h2>
        <p class="text-xs text-base-content/70 leading-relaxed mb-4">
          This policy ensures compliance with Sri Lankan data protection laws and
          protects your data.
        </p>

        <div class="flex flex-col gap-3 pb-4 border-b border-base-200 mb-4">
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i class="fa-regular fa-calendar text-base-content/50 mt-0.5 shrink-0"></i>
            <div>
              <span class="block font-semibold text-base-content">Last Updated</span>
              <span class="block text-base-content/70">{{ (lastUpdated | date:'mediumDate') || 'July 2026' }}</span>
            </div>
          </div>
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i
              class="fa-regular fa-circle-question text-base-content/50 mt-0.5 shrink-0"
            ></i>
            <div>
              <span class="block font-semibold text-base-content">Questions?</span>
              <a
                [href]="'mailto:' + contactEmail"
                class="block text-primary hover:underline break-all"
                >{{ contactEmail }}</a
              >
            </div>
          </div>
        </div>

        <!-- Jump to Section -->
        <div>
          <h3
            class="text-[10px] font-bold tracking-wider text-base-content/50 uppercase mb-2.5"
          >
            JUMP TO SECTION
          </h3>
          <nav class="flex flex-col gap-1">
            <a
              href="privacy#section-1"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >1. Information & Lawful Basis</a
            >
            <a
              href="privacy#section-2"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >2. How We Use Your Data</a
            >
            <a
              href="privacy#section-3"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >3. Data Security & Retention</a
            >
            <a
              href="privacy#section-4"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >4. Cross-Border Transfers</a
            >
            <a
              href="privacy#section-5"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >5. Children's Privacy</a
            >
            <a
              href="privacy#section-6"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >6. Your Privacy Rights</a
            >
            <a
              href="privacy#contact"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >Contact Information</a
            >
          </nav>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="flex-1 min-w-0">
      <p class="text-sm text-base-content/80 leading-relaxed mb-8">
        At {{ academyName }} (the Data Controller), we are
        committed to protecting your personal information. This Privacy Policy
        outlines our practices for collecting, using, safeguarding, and retaining
        your data, ensuring full compliance with the Personal Data Protection Act,
        No. 9 of 2022 (PDPA) of Sri Lanka.
      </p>

      <!-- Section 1 -->
      <section id="section-1" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">1.</span> Information We Collect & Lawful
          Basis
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-4">
          Under Section 5 of the PDPA, we ensure all personal data processing is
          carried out on specified, explicit, and legitimate legal grounds. We
          collect and process the following categories of personal data:
        </p>

        <!-- Categories Table/Cards -->
        <div class="space-y-3 mb-4">
          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4">
            <h3 class="text-sm font-bold text-base-content mb-1">
              Identity & Contact Data
            </h3>
            <p class="text-xs text-base-content/70 mb-2">
              Full name, email address, phone/mobile number, username, and
              profile details provided during registration.
            </p>
            <span
              class="inline-block bg-primary/10 text-primary text-[11px] font-medium px-2.5 py-0.5 rounded-full"
            >
              Lawful Basis: Performance of Contract (Section 5(b))
            </span>
          </div>

          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4">
            <h3 class="text-sm font-bold text-base-content mb-1">
              Subscription & Transaction Data
            </h3>
            <p class="text-xs text-base-content/70 mb-2">
              Payment transaction IDs, billing details, subscription history,
              and invoice records. (We do not store full payment card details;
              payments are handled by licensed payment gateways).
            </p>
            <span
              class="inline-block bg-primary/10 text-primary text-[11px] font-medium px-2.5 py-0.5 rounded-full"
            >
              Lawful Basis: Performance of Contract & Legal Obligation (Section
              5(b), 5(c))
            </span>
          </div>

          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4">
            <h3 class="text-sm font-bold text-base-content mb-1">
              Course & Learning Activity Data
            </h3>
            <p class="text-xs text-base-content/70 mb-2">
              Course enrollments, class attendance, progress tracking, quiz
              scores, and certificates issued.
            </p>
            <span
              class="inline-block bg-primary/10 text-primary text-[11px] font-medium px-2.5 py-0.5 rounded-full"
            >
              Lawful Basis: Performance of Contract (Section 5(b))
            </span>
          </div>

          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4">
            <h3 class="text-sm font-bold text-base-content mb-1">
              Technical & Usage Data
            </h3>
            <p class="text-xs text-base-content/70 mb-2">
              IP addresses, browser type, device information, operating system,
              access times, and pages viewed, collected via cookies and server
              logs.
            </p>
            <span
              class="inline-block bg-success/10 text-success text-[11px] font-medium px-2.5 py-0.5 rounded-full"
            >
              Lawful Basis: Legitimate Interests & Consent (Section 5(a), 5(e))
            </span>
          </div>

          <div class="bg-base-200/50 border border-base-300 rounded-xl p-4">
            <h3 class="text-sm font-bold text-base-content mb-1">
              Third-Party Integration Data (Zoom Marketplace)
            </h3>
            <p class="text-xs text-base-content/70 mb-2">
              When Instructors link a Zoom account via our Zoom Marketplace app,
              we access Zoom OAuth tokens, user profile identifiers (Zoom User
              ID, email), and meeting metadata (meeting IDs, topics, join URLs,
              and start times) strictly to schedule, launch, and manage live
              classes. We do not access meeting audio/video recordings without
              explicit instruction, nor do we access in-meeting chat or
              participant private messages.
            </p>
            <span
              class="inline-block bg-primary/10 text-primary text-[11px] font-medium px-2.5 py-0.5 rounded-full"
            >
              Lawful Basis: Performance of Contract & User Consent (Section
              5(a), 5(b))
            </span>
          </div>
        </div>
      </section>

      <!-- Section 2 -->
      <section id="section-2" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">2.</span> How We Use Your Data
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-3">
          Under the PDPA principles of data minimization and purpose
          specification, we process your personal data strictly for the following
          explicit and legitimate purposes:
        </p>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            Administering your account and providing the educational and
            live-class services of {{ academyName }}.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Processing subscription transactions and issuing invoices.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Tracking progress, grading courses, and generating certificates of
            completion.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Delivering essential platform notifications, system updates, and
            responding to support queries.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            With your explicit consent, sending promotional newsletters and
            marketing communications about new courses or events (which you can
            opt-out of at any time).
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Ensuring platform security, preventing fraudulent activity, and
            conducting network performance analysis.
          </li>
        </ul>
      </section>

      <!-- Section 3 -->
      <section id="section-3" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">3.</span> Data Security & Retention
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-3">
          We implement appropriate technical and organizational measures to
          protect your personal data from unauthorized access, accidental loss,
          destruction, alteration, or disclosure.
        </p>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content">Security Safeguards:</strong> We utilize
            encryption protocols (SSL/TLS), strict role-based access control,
            periodic security reviews, and secure server environments.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content">Data Retention:</strong> We retain your
            personal data only as long as necessary to fulfill the purposes
            detailed in this policy, or as required to meet statutory audit, tax,
            and legal obligations under Sri Lankan law. Once data is no longer
            required, it is securely deleted, anonymized, or destroyed.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content">Data Breach Notification:</strong> In
            the event of a personal data breach that is likely to result in risk
            to your rights and freedoms, we will notify you and the Personal Data
            Protection Authority of Sri Lanka without undue delay, in compliance
            with our statutory duties.
          </li>
        </ul>
      </section>

      <!-- Section 4 -->
      <section id="section-4" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">4.</span> Cross-Border Data Transfers
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-3">
          In compliance with Section 26 of the Personal Data Protection Act of Sri
          Lanka, we govern cross-border processing of your personal data under
          strict requirements:
        </p>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            Our servers and third-party hosting partners may store and process
            data in locations outside of Sri Lanka.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            We ensure that such transfers are carried out in accordance with
            adequacy decisions made by the Personal Data Protection Authority of
            Sri Lanka, or under appropriate safeguards such as Standard
            Contractual Clauses (SCCs), binding corporate rules, or with your
            explicit consent.
          </li>
        </ul>
      </section>

      <!-- Section 5 -->
      <section id="section-5" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">5.</span> Children's Privacy (Under 16
          Years)
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-3">
          The PDPA classifies personal data relating to a child as a Special
          Category of Personal Data requiring enhanced protections.
        </p>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            Under the Sri Lanka PDPA, a "child" is defined as any natural person
            below the age of
            <strong class="text-base-content">sixteen (16) years</strong>.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            We do not knowingly collect or process personal data from children
            under 16 without obtaining verifiable consent from their parent or
            legal guardian.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            In compliance with third-party integration policies (including Zoom App Marketplace guidelines), registration as an Instructor and the linking of third-party Zoom accounts are restricted to users who are at least <strong class="text-base-content">sixteen (16) years of age</strong>. Users under 16 are prohibited from connecting or linking Zoom accounts.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            If you believe we have inadvertently collected data of a child under
            16 without proper parental authorization, please contact us
            immediately, and we will take immediate steps to delete the
            information.
          </li>
        </ul>
      </section>

      <!-- Section 6 -->
      <section id="section-6" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">6.</span> Your Privacy Rights
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed mb-3">
          As a data subject under the Sri Lanka PDPA, you are entitled to several
          statutory rights concerning your personal data:
        </p>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content">Right of Access (Section 13):</strong>
            You have the right to request access to and receive a copy of the
            personal data we hold about you.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content"
              >Right to Withdraw Consent (Section 14):</strong
            >
            You have the right to withdraw your consent to data processing at any
            time, where processing is based on consent.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content"
              >Right to Object to Processing (Section 15):</strong
            >
            You have the right to object to the processing of your data on grounds
            relating to your particular situation, or for direct marketing.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content"
              >Right to Rectification or Completion (Section 16):</strong
            >
            You may request that we correct inaccurate data or complete incomplete
            personal data.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content"
              >Right to Erasure / Right to be Forgotten (Section 17):</strong
            >
            You can request the deletion of your personal data under certain
            conditions, such as when the data is no longer required or processing
            is unlawful.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            <strong class="text-base-content"
              >Right to Object to Automated Decision-Making (Section 18):</strong
            >
            You have the right to object to decisions based solely on automated
            processing, including profiling, which produces legal or significant
            effects.
          </li>
        </ul>
        <p class="text-sm text-base-content/80 leading-relaxed mt-4">
          To exercise any of these rights, please submit a written request to our
          privacy team at
          <a
            [href]="'mailto:' + contactEmail"
            class="text-primary hover:underline"
            >{{ contactEmail }}</a
          >. We will process your request in accordance with statutory guidelines.
        </p>
      </section>

      <!-- Contact Information -->
      <section
        id="contact"
        class="scroll-mt-20 mt-10 pt-8 border-t border-base-200"
      >
        <h2 class="text-lg font-bold text-primary mb-1">
          Contact Our Privacy Team
        </h2>
        <p class="text-sm text-base-content/80 mb-4">
          For privacy-related inquiries, requests to exercise your data subject
          rights, or grievances, please contact our designated Data Protection
          contact.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <a
            [href]="'mailto:' + contactEmail"
            class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content transition duration-150 ease-in-out min-w-[200px] hover:border-primary hover:shadow-sm hover:text-primary"
          >
            <i class="fa-regular fa-envelope text-base-content/50"></i>
            <span>{{ contactEmail }}</span>
          </a>
          <div
            class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content min-w-[200px]"
          >
            <i class="fa-regular fa-clock text-base-content/50"></i>
            <span>Response Time: Within 14 business days</span>
          </div>
        </div>
        <p class="text-xs text-base-content/70 leading-relaxed">
          If you are unsatisfied with how we address your request or grievance,
          you have the right to lodge an appeal or complaint with the
          <strong class="text-base-content"
            >Personal Data Protection Authority of Sri Lanka</strong
          >.
        </p>
      </section>
    </main>
  </div>
}
